/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, lazy } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import {
  PortalInformation,
  formTemplateSource,
  PortalGroups,
  PortalUsersInGroup,
  PortalProjectTemplates,
  PortalSpacesInProjectTemplate,
} from "@Features/portal-settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { CostDatabasePage as CostDatabaseOrig } from "@Features/estimation";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import _ from "lodash";
import Forbidden from "@Pages/Error/Forbidden";
import { setBreadCrumbData } from "@Reducers/breadcrumbsdata";
import { useTranslation } from "@i18n";
import { getPortalProfiles } from "@Actions/portalSetting.helper";
import PermissionGate from "@Pages/Error/PermissionGate";
import type { PortalProfileItem } from "@ServerTypes/portal/portal.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { GroupRow } from "@Features/portal-settings/components/PortalSetings/UserAdminstration/PortalUsers/UsersInGroup/groups.helper";
import type { ProjectTemplateRow } from "@Features/portal-settings/components/PortalSetings/Configuration/ProjectTemplates/projectTemplates.helper";

/**
 * The lazy() callbacks below read named exports off the portal-settings barrel. Its module type is
 * used directly (`PortalSettingsModule`) instead of `any` so a renamed/removed export is caught
 * here; the two `React.ComponentType<...>` aliases cover components whose own props are still
 * loose (`props: any` in the leaf) and which are therefore called with extra props below.
 */
type PortalSettingsModule = typeof import("@Features/portal-settings");

/** One navigable settings tab in the left rail. */
type SettingsTab = {
  text: string;
  value: string;
  ComponentName: React.ReactNode;
  isSelected: boolean;
  /** Optional test hook, set via `tab({ extra })`. */
  dataid?: string;
};

/** One collapsible group of tabs, keyed by its English section name. */
type SettingsSection = {
  list: SettingsTab[];
  expanded: boolean;
  title: string;
};

type SettingsSections = Record<string, SettingsSection>;

const AccessTemplates = lazy(() =>
  import("@Features/portal-settings").then((module) => ({ default: module.portalAccessTemplatesList })),
);
const AccessTemplateDetails = lazy(() =>
  import("@Features/portal-settings").then((module) => ({ default: module.portalAccessTemplateDetails })),
);

const StatusList = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalStatusList })),
);
const Modules = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalModules })),
);
const OrgTypes = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalOrgTypes })),
);
const PortalUsersList = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalUsersList })),
);
const ProfilesList = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalProfilesList })),
);
const ProjectsList = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalProjectsList })),
);
const ProjectUsers = lazy(
  () =>
    import("@Features/portal-settings/components/PortalSetings/UserAdminstration/PortalProjects/ListPortalProjects/ProjectUsers"),
);
const PortalOrganisations = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalOrganisations })),
);
const CostDatabases = lazy(
  () => import("@Features/portal-settings/components/PortalSetings/Estimates/CostDatabases/CostDatabases"),
);
const Units = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalUnits })),
);
const Charges = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalCharges })),
);
const Currencies = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalCurrencies })),
);
const PortalSubscriptionMangement = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({
    default: module.PortalSubscriptionMangementPage,
  })),
);
const MJPPortalSubscription = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({
    default: module.MJPPortalSubscription,
  })),
);
const SsoConfiguration = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.SsoConfiguration })),
);
const TwoFactorAuthConfiguration = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({
    default: module.TwoFactorAuthConfiguration,
  })),
);
const AdminBillingManagement = lazy(() =>
  import("@Features/portal-settings").then((module) => ({ default: module.AdminBillingManagement })),
);
const ActivityLogs = lazy(() => import("@Components/ActivityLogs"));
const FormsAndFields = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.FormsAndFields })),
) as React.ComponentType<{ source?: string }>;
const FormTemplate = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalFormTemplate })),
) as React.ComponentType<{ previewOnly?: boolean }>;
const NewStatusList = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({ default: module.PortalNewStatusList })),
);
const RevisionSeries = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({
    default: module.PortalRevisionSeries,
  })),
);
const ReviewFlowsTemplate = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({
    default: module.PortalReviewFlowsTemplate,
  })),
);
const CheckListSection = lazy(() =>
  import("@Features/portal-settings").then((module: PortalSettingsModule) => ({
    default: module.PortalCheckListSection,
  })),
) as React.ComponentType<{ previewOnly?: boolean; checklistId?: string }>;
const PortalSetting = () => {
  // CostDatabasePage's own props are still untyped upstream; the cast is erased and only covers
  // the `{ loadCostDatabase, costDatabaseId }` bag spread onto it below.
  const CostDatabase = CostDatabaseOrig as React.ComponentType<{
    loadCostDatabase?: boolean;
    costDatabaseId?: string;
  }>;
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const layout = useSelector((state: RootState) => state.layout);
  const userPermissions = useSelector((state: RootState) => state.userPermissions);
  const portalSettingInfo = useSelector((state: RootState) => state.portalSettingInfo);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const portalData = useSelector((state: RootState) => state.portalData);
  const profileData = useSelector((state: RootState) => state.profileData);

  const [hasAccess, setHasAccess] = useState(true);
  const [data, setData] = useState<SettingsSections>({});
  const [selectedTab, setSelectedTab] = useState(params.settingType);
  /**
   * These two were `useState()` with no type argument, i.e. `undefined` — which silently broke the
   * prop contracts of the Groups / UsersInGroup and ProjectTemplates / SpacesInProjectTemplate
   * children (their `setGroupData` / `setProjectTemplateData` props had to be declared
   * `(...args: never[]) => void` and cast at the call site to work around it). They now carry the
   * row types those children actually store.
   */
  const [groupData, setGroupData] = useState<GroupRow | undefined>();
  const [projectTemplateData, setProjectTemplateData] = useState<ProjectTemplateRow | undefined>();

  const superAdminId =
    Array.isArray(profileData) &&
    profileData.find((profile: PortalProfileItem) => profile.name == "PORTAL_SUPER_ADMIN")?.id;

  const isMJPPortal = window.location.host.includes("dpmsportal") || false;

  const breadcrumbMapper: { [key: string]: string } = {
    information: t("Portal Information"),
    ssoConfiguration: t("SSO Configuration"),
    twoFactorAuth: t("Two-Factor Authentication"),
    activityLogs: t("Activity Logs"),
    subscription: t("Billing"),
    adminBilling: t("Admin Billing"),
    users: t("Portal Users"),
    roles: t("Portal Roles"),
    groups: t("Groups"),
    projects: t("Portal Projects"),
    organisations: t("Organizations"),
    modules: t("Modules"),
    statuses: t("Statuses"),
    formsAndFields: t("Forms Templates"),
    reviewFlowsTemplate: t("Review Flows Checklist"),
    accessTemplates: t("Access Templates"),
    projectTemplates: t("Project Templates"),
    OrgTypes: t("Org Unit Types"),
    newStatusList: t("Status List"),
    revisionSeries: t("Revision Series"),
    costDatabases: t("Cost Databases"),
    charges: t("Charges"),
    currencies: t("Currencies"),
    units: t("Units"),
  };

  useEffect(() => {
    dispatch(getPortalProfiles());
  }, []);

  useEffect(() => {
    if (params.settingType) {
      const settingType = params.settingType;
      setSelectedTab(settingType);
      dispatch(setBreadCrumbData([{ text: t("Portal Settings") }, { text: breadcrumbMapper[settingType] }]));

      document.title = `Portal Settings | ${breadcrumbMapper[settingType]}`;
    }
  }, [params.settingType, params.costDatabaseId]);

  useEffect(() => {
    const costDatabasePops = { loadCostDatabase: true, costDatabaseId: params.costDatabaseId };
    const isDpmsPortal = window.location.host.includes("dpmsportal");
    const permissions = _.get(userPermissions, "RESOURCE_TYPE_PORTAL.permissions", []);
    const hasPermission = (perm: string) => _.includes(permissions, perm);
    const hasPortalSubPermission = (userDetails?.portalPermissions ?? []).includes(77) && portalData?.subscription;

    const isSel = (key: string | undefined) => selectedTab === key;
    const tab = ({
      textKey,
      value,
      Component,
      extra = {},
    }: {
      textKey: string;
      value: string;
      Component: React.ReactNode;
      extra?: Partial<Pick<SettingsTab, "dataid">>;
    }): SettingsTab => ({
      text: textKey,
      value,
      ComponentName: Component,
      isSelected: isSel(value.split("?")[0]), // handles things like "modules?selectedTab=default"
      ...extra,
    });

    const buildGeneral = () => {
      const list = [tab({ textKey: t("Portal Information"), value: "information", Component: <PortalInformation /> })];

      const canSeeSubscription = isMJPPortal || hasPortalSubPermission;
      if (canSeeSubscription) {
        list.push(
          tab({
            textKey: t("Billing"),
            value: "subscription",
            Component: isMJPPortal ? <MJPPortalSubscription /> : <PortalSubscriptionMangement />,
          }),
        );
      }

      // Admin Billing tab - only visible to super admins
      const profileId = (userDetails as { profileId?: string } | undefined)?.profileId;
      const isSuperAdmin = profileId === superAdminId || userDetails?.isSuperAdmin;
      if (isSuperAdmin && !isMJPPortal) {
        list.push(
          tab({
            textKey: t("Admin Billing"),
            value: "adminBilling",
            Component: <AdminBillingManagement />,
          }),
        );
      }

      list.push(tab({ textKey: t("Activity Logs"), value: "activityLogs", Component: <ActivityLogs /> }));

      return { list, expanded: true };
    };

    const buildSecurity = () => {
      const list = [
        tab({ textKey: t("SSO Configuration"), value: "ssoConfiguration", Component: <SsoConfiguration /> }),
        tab({
          textKey: t("Two-Factor Authentication"),
          value: "twoFactorAuth",
          Component: <TwoFactorAuthConfiguration />,
        }),
      ];
      return { list, expanded: true };
    };

    const buildUserAdministration = () => {
      const list = [
        tab({
          textKey: t("Portal Users"),
          value: "users",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.portalUsers?.isVisible}>
              <PortalUsersList />
            </PermissionGate>
          ),
          extra: { dataid: "portalUsers" },
        }),
        tab({
          textKey: t("Portal Roles"),
          value: "roles",
          Component: hasPermission("VIEW_LIST_OF_PORTAL_ROLES") ? <ProfilesList /> : <Forbidden />,
          extra: { dataid: "portalRoles" },
        }),
      ];

      if (!isDpmsPortal) {
        list.push(
          tab({
            textKey: t("Groups"),
            value: "groups",
            Component: (
              <PermissionGate allowed={portalSettingInfo?.groups?.isVisible}>
                {params.costDatabaseId ? (
                  <PortalUsersInGroup groupData={groupData} setGroupData={setGroupData} />
                ) : (
                  <PortalGroups setGroupData={setGroupData} />
                )}
              </PermissionGate>
            ),
            extra: { dataid: "groups" },
          }),
        );
      }

      return { list, expanded: true };
    };

    const buildProjectAdministration = () => {
      const projectComponent = (
        <PermissionGate allowed={portalSettingInfo?.portalProjects?.isVisible}>
          {params.projectId ? (
            // useParams gives a plain string; ProjectUsers declares the branded ObjectIdString.
            // Cast is erased.
            <ProjectUsers source={"portalSetting"} projectId={params.projectId as ObjectIdString} />
          ) : (
            <ProjectsList />
          )}
        </PermissionGate>
      );

      const list = [
        tab({
          textKey: t("Portal Projects"),
          value: "projects",
          Component: projectComponent,
          extra: { dataid: "portalProjects" },
        }),
        tab({
          textKey: t("Organizations"),
          value: "organisations",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.organisations?.isVisible}>
              <PortalOrganisations />
            </PermissionGate>
          ),
          extra: { dataid: "organizations" },
        }),
        // Keep commented custom fields as-is if you re-enable later
      ];

      return { list, expanded: true };
    };

    const buildConfiguration = () => {
      if (isMJPPortal) return null;

      const list = [
        tab({
          textKey: t("Modules"),
          value: "modules?selectedTab=default",
          Component: <Modules />,
        }),
        tab({
          textKey: t("Status"),
          value: "statuses",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.status?.isVisible}>
              <StatusList />
            </PermissionGate>
          ),
        }),
        tab({
          textKey: t("Forms Template"),
          value: "formsAndFields?selectedCategory=inspection&selectedTab=formTemplates",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.formTemplates?.isVisible}>
              {!params.formId ? (
                <FormsAndFields source={formTemplateSource.portalSettings} />
              ) : (
                <FormTemplate previewOnly={!portalSettingInfo?.formTemplates?.editFormTemplates?.enable} />
              )}
            </PermissionGate>
          ),
        }),
        tab({
          textKey: t("Review Flows Checklist"),
          value: "reviewFlowsTemplate",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.reviewFlowsChecklist?.isVisible}>
              {!params.checklistId ? (
                <ReviewFlowsTemplate />
              ) : (
                <CheckListSection
                  previewOnly={!portalSettingInfo?.reviewFlowsChecklist?.editReviewFlowsChecklist?.enable}
                  checklistId={params.checklistId}
                />
              )}
            </PermissionGate>
          ),
        }),
        tab({
          textKey: t("Project Templates"),
          value: "projectTemplates",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.projectTemplates?.isVisible}>
              {!params.costDatabaseId ? (
                <PortalProjectTemplates setProjectTemplateData={setProjectTemplateData} />
              ) : (
                <PortalSpacesInProjectTemplate
                  projectTemplateData={projectTemplateData}
                  setProjectTemplateData={setProjectTemplateData}
                />
              )}
            </PermissionGate>
          ),
        }),
        tab({
          textKey: t("Access Templates"),
          value: "accessTemplates",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.accessTemplate?.isVisible}>
              {params.costDatabaseId ? (
                <AccessTemplateDetails accessTemplateId={params.costDatabaseId} />
              ) : (
                <AccessTemplates />
              )}
            </PermissionGate>
          ),
        }),
        tab({
          textKey: t("Org Unit Types"),
          value: "OrgTypes",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.orgUnitTypes?.isVisible}>
              <OrgTypes />
            </PermissionGate>
          ),
        }),
        tab({ textKey: t("Status List"), value: "newStatusList", Component: <NewStatusList /> }),
        tab({
          textKey: t("Revision Series"),
          value: "revisionSeries",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.revisionSeries?.isVisible}>{<RevisionSeries />}</PermissionGate>
          ),
        }),
      ];

      return { list, expanded: true };
    };

    const buildEstimates = () => {
      if (!layout.ESTIMATES?.ENABLE) return null;

      const list = [
        tab({
          textKey: t("Cost Databases"),
          value: "costDatabases",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.costDatabase?.isVisible}>
              {!params.costDatabaseId && !params.costDatabaseVersionId ? (
                <CostDatabases />
              ) : (
                <CostDatabase {...costDatabasePops} />
              )}
            </PermissionGate>
          ),
          extra: { dataid: "costDatabases" },
        }),
        tab({
          textKey: t("Charges"),
          value: "charges",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.charges?.isVisible}>
              <Charges />
            </PermissionGate>
          ),
          extra: { dataid: "charges" },
        }),
        tab({
          textKey: t("Units"),
          value: "units",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.units?.isVisible}>
              <Units />
            </PermissionGate>
          ),
          extra: { dataid: "units" },
        }),
        tab({
          textKey: t("Currencies"),
          value: "currencies",
          Component: (
            <PermissionGate allowed={portalSettingInfo?.currencies?.isVisible}>
              <Currencies />
            </PermissionGate>
          ),
          extra: { dataid: "currencies" },
        }),
      ];

      return { list, expanded: true };
    };

    const assembled: SettingsSections = {
      General: { ...buildGeneral(), title: t("General") },
      Security: { ...buildSecurity(), title: t("Security") },
      "User Administration": { ...buildUserAdministration(), title: t("User Administration") },
      "Project Administration": { ...buildProjectAdministration(), title: t("Project Administration") },
    };

    const configuration = buildConfiguration();
    if (configuration) assembled["Configuration"] = { ...configuration, title: t("Configuration") };

    const estimates = buildEstimates();
    if (estimates) assembled["Estimates"] = { ...estimates, title: t("Estimates") };

    setData(assembled);
  }, [
    layout.ESTIMATES,
    params,
    selectedTab,
    userPermissions,
    superAdminId,
    userDetails,
    portalData?.subscription,
    portalSettingInfo,
  ]);

  useEffect(() => {
    const permissions = _.get(userPermissions, "RESOURCE_TYPE_PORTAL.permissions", []);
    const isUserAllowed = _.includes(permissions, "ACCESS_PORTAL_SETTINGS");
    if (!isUserAllowed) {
      setHasAccess(false);
    } else {
      setHasAccess(true);
    }
  }, [userPermissions]);

  const onSettingClick = (settingType: string) => () => {
    navigate(`/portalSettings/${settingType}`);
  };

  const handleAccordionChange = (type: string) => () => {
    setData({
      ...data,
      [type]: {
        // `!` is erased: `type` always comes from Object.keys(data) in the render below.
        ...data[type]!,
        expanded: !data[type]!.expanded,
      },
    });
  };

  return hasAccess ? (
    <div className="flex h-full w-full overflow-auto">
      <div className={`overflow-auto flex-none z-0 h-full w-64 text-sm px-6`}>
        {Object.keys(data).map((eachData: string) => (
          <Accordion
            sx={{
              "& .MuiListItemButton-root": {
                marginInlineStart: "-2rem",
                paddingInlineStart: "3rem",
                marginInlineEnd: "-2rem",
              },
            }}
            className="bg-transparent"
            key={`accordion_${eachData}`}
            expanded={data[eachData]!.expanded}
            onChange={handleAccordionChange(eachData)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <span className="font-semibold">{data[eachData]!.title}</span>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <List component="nav">
                  {data[eachData]!.list.map((arg: SettingsTab) => (
                    <ListItemButton
                      key={`listItem_${arg.text}`}
                      selected={arg.isSelected}
                      onClick={onSettingClick(arg.value)}
                    >
                      <ListItemText
                        primary={
                          <span className="text-sm" data-testid={arg.text}>
                            {arg.text}
                          </span>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>

      <div className="me-5 ms-4 flex h-full w-full overflow-auto rounded-t-lg bg-[var(--surface-white)] shadow-light">
        {Object.keys(data).map((eachData: string) => {
          return data[eachData]!.list.map(({ ComponentName, isSelected }) => (isSelected ? ComponentName : null));
        })}
      </div>
    </div>
  ) : (
    <Forbidden />
  );
};

export default PortalSetting;
